// PR-MAY16-INVESTOR-RATES-V2 item 3 — per-investor (per-site) rate hooks.
//
// Replaces yesterday's useInvestorVehicleRates as the primary rate
// hook surfaced in the drawer. The vehicle-rates hook still exists
// for the Vehicles section's override panel.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

// PR-MAY16-RATE-DROPDOWN-FIX — keyed on siteId (not siteContractId).
export interface InvestorSiteRateRow {
  id: string;
  site_id: string;
  site_name: string;
  investor_id: string;
  investor_rate_at_target: string;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
}

export interface InvestorSiteRateInput {
  siteId: string;
  investorId: string;
  investorRateAtTarget: number;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  notes?: string | null;
}

export function useInvestorSiteRates(investorId: string | undefined) {
  return useQuery({
    queryKey: ['investor-site-rates', investorId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: InvestorSiteRateRow[] }>(
        `/api/v1/investor-rates/site/by-investor/${investorId}`,
      );
      return data.items;
    },
    enabled: Boolean(investorId),
  });
}

export function useCreateInvestorSiteRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InvestorSiteRateInput) => {
      const { data } = await apiClient.post<{ rate: InvestorSiteRateRow }>(
        '/api/v1/investor-rates/site',
        input,
      );
      return data.rate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-site-rates', investorId] });
    },
  });
}

export function useUpdateInvestorSiteRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<InvestorSiteRateInput> }) => {
      const { data } = await apiClient.patch<{ rate: InvestorSiteRateRow }>(
        `/api/v1/investor-rates/site/${id}`,
        input,
      );
      return data.rate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-site-rates', investorId] });
    },
  });
}

// PR-MAY16-RATE-SAVE-FIX — UPSERT today's row. Idempotent. Replaces the
// FE's create-vs-update dance and guarantees the row is currently
// active (effective_from <= today, effective_to=null).
export function useUpsertInvestorSiteRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      siteId: string;
      investorId: string;
      investorRateAtTarget: number;
      effectiveFrom?: string;
      notes?: string | null;
    }) => {
      const { data } = await apiClient.post<{ rate: InvestorSiteRateRow }>(
        '/api/v1/investor-rates/site/upsert',
        input,
      );
      return data.rate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-site-rates', investorId] });
    },
  });
}

export function useDeleteInvestorSiteRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(
        `/api/v1/investor-rates/site/${id}`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-site-rates', investorId] });
    },
  });
}

// PR-MAY16-INVESTOR-RATES-V2 item 4 — bulk apply ONE rate to MANY
// contracts. Each contract that already has a (investor, effective_from)
// row at the same date is skipped (the API replies with a 207 + skip
// reasons).
export function useBulkApplyInvestorSiteRate(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      investorId: string;
      siteIds: string[];
      investorRateAtTarget: number;
      effectiveFrom?: string;
      notes?: string | null;
    }) => {
      const { data } = await apiClient.post<{
        requested: number; created: number; skipped: number;
        details: { created: Array<{ id: string; siteId: string }>;
                   skipped: Array<{ siteId: string; reason: string }>; };
      }>('/api/v1/investor-rates/site/bulk', input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-site-rates', investorId] });
    },
  });
}

// PR-MAY16-INVESTOR-RATES-V2 item 4 — copy entire rate sheet from
// another investor (the "template"). overwrite=false skips duplicates;
// overwrite=true updates the existing row's rate.
export function useCopyRatesFromInvestor(investorId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      targetInvestorId: string;
      sourceInvestorId: string;
      overwrite?: boolean;
    }) => {
      const { data } = await apiClient.post<{
        source: string; target: string;
        sourceRowCount: number;
        created: number; updated: number; skipped: number;
      }>('/api/v1/investor-rates/site/copy-from', input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investor-site-rates', investorId] });
    },
  });
}
